import {
    ClientCampaignBooking,
    ClientCampaignInstagramPostStats,
    ClientCampaignInstagramPostStatsTotals,
    InstagramUserImage,
} from '@round/api';
import { useMemo } from 'react';
import Table from '../../../../../ui/Table/Table';
import { Column, Row } from 'react-table';
import { Image } from '@round/ui-kit';
import styles from './PostStatsTable.module.css';
import { displayOptionalNumericTableValue, numberWithCommas } from '../../../../../utils/valueFormatHelpers';
import { buildInstagramUserUrl } from '../../../../../utils/utits';
import { renderPostStatsTableBody } from './PostStatsTable.helpers';
import uniqBy from 'lodash/uniqBy';
import StatusPicker from '../../../../booking/TableCells/StatusPicker/StatusPicker';

export type InstagramPostStatsTableRow = Partial<ClientCampaignInstagramPostStats> & {
    booking: ClientCampaignBooking;
    userImage: InstagramUserImage | null;
};

type InstagramPostStatsTableProps = {
    loading: boolean;
    error: boolean;
    data: InstagramPostStatsTableRow[];
    totals: ClientCampaignInstagramPostStatsTotals | null;
};

const InstagramPostStatsTable = ({ loading, error, data, totals }: InstagramPostStatsTableProps) => {
    const columns: Column<InstagramPostStatsTableRow>[] = useMemo(
        () => [
            {
                Header: 'Account',
                id: 'account',
                Cell: ({ row }: { row: Row<InstagramPostStatsTableRow> }) => {
                    const username = row.original.booking.influencer.instagram_username;
                    const userProfileUrl = username ? buildInstagramUserUrl(username) : '';

                    const userImage = row.original.userImage;
                    const imageUrl = userImage?.avatar_thumb.cached_url || userImage?.avatar_thumb.original_url || '';

                    return (
                        <div className={styles.accountCell}>
                            <Image src={imageUrl} className={styles.userImage} alt="user" />
                            {username ? (
                                <a href={userProfileUrl} target="_blank" rel="noreferrer">
                                    {username}
                                </a>
                            ) : (
                                <span>-</span>
                            )}
                        </div>
                    );
                },
            },
            {
                Header: 'Status',
                id: 'status',
                Cell: ({ row }: { row: Row<InstagramPostStatsTableRow> }) => {
                    const status = row.original.booking.status;

                    return <StatusPicker readonly status={status} />;
                },
            },
            {
                Header: 'Followers',
                id: 'followers',
                Cell: ({ row }: { row: Row<InstagramPostStatsTableRow> }) => {
                    const followerCount = row.original.booking.influencer.instagram_follower_count;

                    return <>{displayOptionalNumericTableValue(followerCount)}</>;
                },
            },
            {
                Header: 'Video Creation Time',
                accessor: 'create_time',
                Cell: ({ value: createTime }) => {
                    if (!createTime) {
                        return <>-</>;
                    }

                    const date = new Date(createTime);
                    const dateLiteral = date.toLocaleString('en-US');
                    return <>{dateLiteral}</>;
                },
            },
            {
                Header: 'Views',
                accessor: 'total_views',
                Cell: ({ value: totalViews }) => {
                    return <>{displayOptionalNumericTableValue(totalViews)}</>;
                },
            },
            {
                Header: 'Likes',
                accessor: 'likes',
                Cell: ({ value: likes }) => <>{displayOptionalNumericTableValue(likes)}</>,
            },
            {
                Header: 'Comments',
                accessor: 'comments',
                Cell: ({ value: comments }) => <>{displayOptionalNumericTableValue(comments)}</>,
            },
        ],
        []
    );

    const totalFollowerCount = useMemo(
        () =>
            uniqBy(data, (row) => row.booking.influencer.id)
                .map((row) => row.booking.influencer.instagram_follower_count || 0)
                .reduce((agg, followers) => agg + followers, 0),
        [data]
    );

    return (
        <Table
            columns={columns}
            data={data}
            className={styles.table}
            renderBody={renderPostStatsTableBody(loading, error)}
            renderFooter={() => (
                <tfoot>
                    <tr>
                        <td>TOTAL</td>
                        <td />
                        <td>{totalFollowerCount ? numberWithCommas(totalFollowerCount) : '-'}</td>
                        <td />
                        <td>{totals?.total_views ? numberWithCommas(totals.total_views) : '-'}</td>
                        <td>{totals?.likes ? numberWithCommas(totals.likes) : '-'}</td>
                        <td>{totals?.comments ? numberWithCommas(totals.comments) : '-'}</td>
                    </tr>
                </tfoot>
            )}
        />
    );
};

export default InstagramPostStatsTable;
